import { GomakePasswordInput } from "@/components";
import { PrimaryButton } from "@/components/button/primary-button";
import { CloseIcon } from "@/widgets/quote-new/receipts-table/Payment-methods-details/icons/close-icon";
import { Box, Modal, Typography, IconButton, Stack } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { setUserNewPasswordApi } from "@/services/api-service/quick-setup-menu/reset-password";
import { useGomakeAxios, useSnackBar } from "@/hooks";
import { useStyle } from "./style";
import { useRecoilValue, useSetRecoilState } from "recoil";
import {
  printHouseSetupStepsState,
  printHouseWelcomeModalState,
  setupGuideExpandedState,
  SetupStep,
  SetupStepItem,
  StepStatus,
} from "@/store/print-house-setup-state-state";
import {
  EMPLOYEE_STRONG_PASSWORD_REGEX_STRING,
  isEmployeePasswordStrong,
} from "@/utils/employee-password-policy";

interface ResetPasswordModalProps {
  openModal: boolean;
  onClose: () => void;
}

export const ResetPasswordModal = ({
  openModal,
  onClose,
}: ResetPasswordModalProps) => {
  const { classes } = useStyle();
  const { t } = useTranslation();
  const { callApi } = useGomakeAxios();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const { alertSuccessUpdate, alertFaultUpdate } = useSnackBar();
  const setIsExpanded = useSetRecoilState(setupGuideExpandedState);
  const setPrintHouseWelcomeModal = useSetRecoilState<boolean>(
    printHouseWelcomeModalState
  );
  const printHouseSetupSteps = useRecoilValue<SetupStepItem[]>(
    printHouseSetupStepsState
  );

  const newPasswordInvalid =
    newPassword.length > 0 && !isEmployeePasswordStrong(newPassword);
  const confirmMismatch =
    confirmPassword.length > 0 &&
    newPassword.length > 0 &&
    newPassword !== confirmPassword;

  const canSubmit = useMemo(() => {
    return (
      newPassword.trim().length > 0 &&
      confirmPassword.trim().length > 0 &&
      isEmployeePasswordStrong(newPassword) &&
      newPassword === confirmPassword
    );
  }, [newPassword, confirmPassword]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!isEmployeePasswordStrong(newPassword)) {
      setError(t("usersSettings.passwordNotStrongEnough"));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t("quickSetup.resetPass.passwordsDoNotMatch"));
      return;
    }
    setTimeout(() => {
      resetPassword(newPassword);
      setError("");
    }, 500);
  };

  const handleCloseModal = () => {
    onClose();
    setNewPassword("");
    setConfirmPassword("");
    setError("");
  };

  const resetPassword = async (newPassword: string) => {
    const callBack = (res: { success: boolean }) => {
      if (res.success) {
        handleCloseModal();
        alertSuccessUpdate();

        const welcomeStep = printHouseSetupSteps.find(
          (step) => step.step === SetupStep.WELCOME_MESSAGE
        );
        if (welcomeStep?.status === StepStatus.NOT_YET) {
          setTimeout(() => {
            setPrintHouseWelcomeModal(true);
          }, 200);
        }

        setTimeout(() => {
          setIsExpanded(true);
        }, 200);
      } else {
        alertFaultUpdate();
      }
    };
    await setUserNewPasswordApi(callApi, callBack, { newPassword });
  };

  const clearErrorOnType = () => {
    if (error) setError("");
  };

  return (
    <Modal open={openModal} onClose={handleCloseModal}>
      <Box sx={classes.mainBox}>
        <Box sx={classes.headerBox}>
          <Typography variant="h6">
            {t("quickSetup.resetPass.title")}
          </Typography>

          <IconButton onClick={() => handleCloseModal()}>
            <CloseIcon />
          </IconButton>
        </Box>

        <form onSubmit={handleSubmit}>
          <Stack spacing={2} sx={{ width: "100%", minWidth: 0 }}>
            <Stack spacing={0.75} sx={{ width: "100%", minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {t("quickSetup.resetPass.newPassword")}
              </Typography>
              <GomakePasswordInput
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  clearErrorOnType();
                }}
                inputId="quick-setup-reset-new-password"
                name="new-password"
                feedback
                appendTo="self"
                strongRegex={EMPLOYEE_STRONG_PASSWORD_REGEX_STRING}
                promptLabel={t("usersSettings.passwordMeterPrompt")}
                weakLabel={t("usersSettings.passwordMeterWeak")}
                mediumLabel={t("usersSettings.passwordMeterMedium")}
                strongLabel={t("usersSettings.passwordMeterStrong")}
                header={
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 13,
                      marginBottom: 4,
                      maxWidth: "100%",
                      overflowWrap: "break-word",
                    }}
                  >
                    {t("usersSettings.passwordPolicyTitle")}
                  </div>
                }
                footer={
                  <div
                    style={{
                      fontSize: 12,
                      color: "#666",
                      marginTop: 10,
                      lineHeight: 1.45,
                      maxWidth: "100%",
                      overflowWrap: "break-word",
                    }}
                  >
                    {t("usersSettings.passwordPolicyFooter")}
                  </div>
                }
                toggleMask
                autoComplete="new-password"
                placeholder={t("quickSetup.resetPass.newPassword")}
                error={newPasswordInvalid}
              />
              {newPasswordInvalid ? (
                <Typography color="error" variant="caption" display="block">
                  {t("usersSettings.passwordNotStrongEnough")}
                </Typography>
              ) : null}
            </Stack>

            <Stack spacing={0.75} sx={{ width: "100%", minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {t("quickSetup.resetPass.confirmPassword")}
              </Typography>
              <GomakePasswordInput
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  clearErrorOnType();
                }}
                inputId="quick-setup-reset-confirm-password"
                name="confirm-new-password"
                feedback={false}
                toggleMask
                autoComplete="new-password"
                placeholder={t("quickSetup.resetPass.confirmPassword")}
                error={confirmMismatch}
              />
            </Stack>
          </Stack>

          {error && (
            <Typography color="error" sx={{ mb: 1, mt: 1 }}>
              {error}
            </Typography>
          )}
          <PrimaryButton
            type="submit"
            variant="contained"
            fullWidth
            sx={{ mb: 1, mt: 2 }}
            disabled={!canSubmit}
          >
            {t("quickSetup.resetPass.resetButton")}
          </PrimaryButton>
        </form>
      </Box>
    </Modal>
  );
};
